import React, {type Dispatch, type SetStateAction, useCallback, useEffect, useMemo} from 'react';


import {EMAIL_SETTINGS} from 'src/constants';
import {useAccountManagerTypes} from 'src/hooks/dictionaries';
import {EmailType as EmailTypes} from 'src/openapi/admin/models/EmailType';

import {CheckBox} from '@pb/kit-component-checkbox';
import {Group} from '@pb/kit-component-group';

import type {AccountManagerInfo} from 'src/openapi/admin/models/AccountManagerInfo';
import type {EmailType} from 'src/openapi/admin/models/EmailType';
import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    emailType: EmailType,
    teamMembers: AccountManagerInfo[],
    selectedTeamMembers: AccountManagerInfo[],
    selectTeamMembers: Dispatch<SetStateAction<AccountManagerInfo[]>>,
};

export const SelectTeamBlock = (props: Props) => {
    const {
        emailType,
        selectedTeamMembers,
        selectTeamMembers,
        teamMembers,
    } = props;
    const {getAccountManagerTypeByCode} = useAccountManagerTypes();

    const isTeamConfigured = useMemo(() => {
        const result = teamMembers.length <= 2 &&
            teamMembers.find(({managerType}) => managerType === 'AM') &&
            teamMembers
                .filter(({managerType}) => managerType)
                .map(({managerType}) => managerType ?? '')
                .every(managerType => ['PRIMARY_CSM', 'AM'].includes(managerType));

        if (result) {
            selectTeamMembers(teamMembers);
        }

        return result;
    }, [teamMembers, selectTeamMembers]);

    useEffect(() => {
        if (emailType === EmailTypes.WELCOME_EMAIL) {
            selectTeamMembers([]);
        }
    }, [emailType, selectTeamMembers]);

    useEffect(() => {
        if (emailType === EmailTypes.WELCOME_EMAIL) {
            selectTeamMembers([]);
        } else if (isTeamConfigured) {
            selectTeamMembers(teamMembers);
        }
    }, [isTeamConfigured, teamMembers, emailType, selectTeamMembers]);

    const {amCsmRequired, supportFallback, confirmMessage, noTeamConfirmMessage, infoPopover}
        = EMAIL_SETTINGS[emailType];
    const hasAccountTeam = useMemo(() => teamMembers.some(({managerType}) => managerType), [teamMembers]);
    // Emails with a Support fallback keep the selection optional, but the admin may still pick contacts
    const showTeamSelection = hasAccountTeam && !isTeamConfigured && (amCsmRequired || supportFallback);
    const message = !hasAccountTeam && noTeamConfirmMessage
        ? noTeamConfirmMessage
        : confirmMessage ?? infoPopover;

    const changeEmailAccountTeam = useCallback((changeData: CheckBoxChangeData) => {
        const {event, name} = changeData;
        const changedMember = teamMembers.find(({id}) => id.toString() === name);

        selectTeamMembers(prevState => [
            ...prevState.filter(({id}) => id.toString() !== name),
            ...event.currentTarget.checked && changedMember ? [changedMember] : [],
        ]);
    }, [teamMembers, selectTeamMembers]);

    return (
        <Group direction="v" alignItems="start">
            <p>{message}</p>
            <p>Do you want to send the email?</p>
            {showTeamSelection ? (
                <Group direction="v" alignItems="start">
                    <p>Please select the account managers that will be mentioned in the email:</p>
                    {teamMembers.filter(({managerType}) => managerType).map(teamMember => (
                        <CheckBox
                            checked={selectedTeamMembers.map(({id}) => id).includes(teamMember.id)}
                            name={teamMember.id.toString()}
                            key={teamMember.id}
                            label={`
                            ${teamMember.fullName}
${getAccountManagerTypeByCode(teamMember?.managerType ?? '')?.description}`}
                            onCheckBoxChange={changeEmailAccountTeam}
                        />
                    ))}
                </Group>
            ) : null}
        </Group>
    );
};
